import React from 'react';
import { Text as RNText, TextProps as RNTextProps, TextStyle } from 'react-native';
import { componentThemes } from '../../theme/componentThemes';
import { getTypographyVariant, parseValue } from '../../theme/themeUtils';
import { tokens } from '../../theme/tokens';

// Text variant type
type TextVariant =
  | 'xxxl-normal'
  | 'xxl-normal'
  | 'xl-normal'
  | 'lg-normal'
  | 'md-normal'
  | 'sm-normal'
  | 'xsm-normal'
  | 'xxxl-bold'
  | 'xxl-bold'
  | 'xl-bold'
  | 'lg-bold'
  | 'md-bold'
  | 'sm-bold'
  | 'xsm-bold'
  | 'disabled';

// Text size type
type TextSize = 'xxxl' | 'xxl' | 'xl' | 'lg' | 'md' | 'sm' | 'xsm';

// Extended Text props interface
interface TextProps extends RNTextProps {
  // NativeBase specific props
  variant?: TextVariant;
  size?: TextSize;

  // Color props
  color?: string;

  // Font weight
  fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';

  // Text alignment
  textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify';

  // Text decoration
  textDecorationLine?: 'none' | 'underline' | 'line-through' | 'underline line-through';
  underline?: boolean; // NativeBase shortcut for underline

  // Layout props
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  flex?: number;

  // Spacing props (NativeBase shortcuts)
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  marginX?: number | string; // marginHorizontal
  marginY?: number | string; // marginVertical
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
  paddingX?: number | string; // paddingHorizontal
  paddingY?: number | string; // paddingVertical

  // NativeBase theme variants (we'll ignore these for now)
  _dark?: any;
  _light?: any;
  _hover?: any;
  _focus?: any;
  _pressed?: any;
  _disabled?: any;
}

const Text: React.FC<TextProps> = ({
  variant,
  size,
  color,
  fontWeight,
  textAlign,
  textDecorationLine,
  underline,
  alignSelf,
  flex,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  marginX,
  marginY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingX,
  paddingY,
  style,
  children,
  // Ignore theme variants
  _dark,
  _light,
  _hover,
  _focus,
  _pressed,
  _disabled,
  ...rest
}) => {
  const textTheme = componentThemes.text;

  // Debug logging for tests
  if (process.env.NODE_ENV === 'test') {
    console.log('Text component debug:', {
      textTheme,
      variant,
      size,
      tokens: tokens.typography.fontSizes.md,
      xxxlSize: tokens.typography.fontSizes.xxxl,
    });
  }

  // Get font size and line height based on variant or size
  const getFontSize = (): { fontSize: number; lineHeight: number } => {
    if (variant) {
      // Use theme system to get typography variant
      const typographyStyle = getTypographyVariant(variant);
      return {
        fontSize: typographyStyle.fontSize || textTheme.sizes.md.fontSize,
        lineHeight: typographyStyle.lineHeight || textTheme.sizes.md.lineHeight,
      };
    }

    if (size) {
      return {
        fontSize: tokens.typography.fontSizes[size],
        lineHeight: tokens.typography.lineHeights[size],
      };
    }

    // Default to medium size when no variant or size is specified
    return {
      fontSize: tokens.typography.fontSizes.md,
      lineHeight: tokens.typography.lineHeights.md,
    };
  };

  // Get font family based on variant or fontWeight
  const getFontFamily = (): string => {
    if (variant) {
      const typographyStyle = getTypographyVariant(variant);
      if (typographyStyle.fontFamily) {
        return typographyStyle.fontFamily;
      }
    }

    if (fontWeight === 'bold' || fontWeight === '700' || fontWeight === '800' || fontWeight === '900') {
      return tokens.typography.fontFamilies.bold;
    }

    return tokens.typography.fontFamilies.regular;
  };

  // Get color
  const getColor = (): string => {
    if (variant === 'disabled') {
      return textTheme?.variants?.disabled?.color || tokens.colors.neutral.cotton;
    }
    return color || tokens.colors.neutral.charcoal;
  };

  const fontSizeStyles = getFontSize();
  const fontFamily = getFontFamily();
  const textColor = getColor();

  const textStyle: TextStyle = {
    // Always apply default styles
    fontSize: fontSizeStyles.fontSize || tokens.typography.fontSizes.md,
    lineHeight: fontSizeStyles.lineHeight || tokens.typography.lineHeights.md,
    fontFamily: fontFamily || tokens.typography.fontFamilies.regular,
    color: textColor || tokens.colors.neutral.charcoal,
    fontWeight,
    textAlign,
    textDecorationLine: underline ? 'underline' : textDecorationLine,
    alignSelf,
    flex,
    // Spacing
    marginTop: parseValue(marginTop),
    marginBottom: parseValue(marginBottom),
    marginLeft: parseValue(marginLeft),
    marginRight: parseValue(marginRight),
    marginHorizontal: parseValue(marginX),
    marginVertical: parseValue(marginY),
    paddingTop: parseValue(paddingTop),
    paddingBottom: parseValue(paddingBottom),
    paddingLeft: parseValue(paddingLeft),
    paddingRight: parseValue(paddingRight),
    paddingHorizontal: parseValue(paddingX),
    paddingVertical: parseValue(paddingY),
  };

  return (
    <RNText style={[textStyle, style]} {...rest}>
      {children}
    </RNText>
  );
};

export default Text;
